import { zillowApi } from "api/zillow";
import { zumperApi } from "api/zumper";
import { apartmentsComApi } from "api/apartmentsCom";
import { boomFiApi } from "api/boom-finance";
import { useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { useAuthContext } from "providers/Auth";
import { Button, ButtonSize, ButtonVariant, Radio } from "shared/components";
import { Card } from "shared/components/Card";
import GoogleAnalyticsIcon from "shared/icons/brands/GoogleAnalytics-brand-icon.svg?react";
import HubSpotIcon from "shared/icons/brands/hubspot.svg?react";
import { cx } from "shared/utils";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import {
  IntegrationId,
  Integration,
  Step,
  DIRECT_CONNECT_INTEGRATIONS
} from "views/settings-integration/components/AddIntegrationModal/types";
import { IntegrationLogoIcon } from "views/settings-integration/components/IntegrationLogoIcon";
import { useSettingsController } from "views/settings-integration/controller";
import { PMSNames } from "views/settings-integration/integrations/pms/constants";
import { PropertyManagementSystem } from "shared/types/partner";
import { FEATURES } from "types/landlord/partner";

interface FirstStepProps {
  selectedIntegration: PropertyManagementSystem | IntegrationId;
  setSelectedIntegration: (
    id: PropertyManagementSystem | IntegrationId
  ) => void;
  setCurrentStep: (step: Step) => void;
  closeModal: () => void;
}

const BASE_INTEGRATIONS: Integration[] = [
  {
    id: IntegrationId.GA,
    name: "Google Analytics",
    icon: <GoogleAnalyticsIcon />
  },
  {
    id: IntegrationId.HUBSPOT,
    name: "HubSpot",
    icon: <HubSpotIcon />
  }
];

const PMS_INTEGRATIONS: Integration[] = [
  {
    id: PropertyManagementSystem.ManageAmerica,
    name: PMSNames[PropertyManagementSystem.ManageAmerica],
    icon: (
      <IntegrationLogoIcon
        id={PropertyManagementSystem.ManageAmerica}
        size="BASE"
      />
    )
  },
  {
    id: PropertyManagementSystem.Buildium,
    name: PMSNames[PropertyManagementSystem.Buildium],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Buildium} size="BASE" />
    )
  },
  {
    id: PropertyManagementSystem.Appfolio,
    name: PMSNames[PropertyManagementSystem.Appfolio],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Appfolio} size="BASE" />
    )
  },
  {
    id: PropertyManagementSystem.Yardi,
    name: PMSNames[PropertyManagementSystem.Yardi],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Yardi} size="BASE" />
    )
  },
  {
    id: PropertyManagementSystem.RentManager,
    name: PMSNames[PropertyManagementSystem.RentManager],
    icon: (
      <IntegrationLogoIcon
        id={PropertyManagementSystem.RentManager}
        size="BASE"
      />
    )
  },
  {
    id: PropertyManagementSystem.Resman,
    name: PMSNames[PropertyManagementSystem.Resman],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Resman} size="BASE" />
    )
  },
  {
    id: PropertyManagementSystem.Rentvine,
    name: PMSNames[PropertyManagementSystem.Rentvine],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Rentvine} size="BASE" />
    )
  },
  {
    id: PropertyManagementSystem.Quext,
    name: PMSNames[PropertyManagementSystem.Quext],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Quext} size="BASE" />
    )
  },
  {
    id: PropertyManagementSystem.Entrata,
    name: PMSNames[PropertyManagementSystem.Entrata],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Entrata} size="BASE" />
    )
  },
  {
    id: PropertyManagementSystem.RentersWarehouse,
    name: PMSNames[PropertyManagementSystem.RentersWarehouse],
    icon: (
      <IntegrationLogoIcon
        id={PropertyManagementSystem.RentersWarehouse}
        size="BASE"
      />
    )
  },
  {
    id: PropertyManagementSystem.Propertyware,
    name: PMSNames[PropertyManagementSystem.Propertyware],
    icon: (
      <IntegrationLogoIcon
        id={PropertyManagementSystem.Propertyware}
        size="BASE"
      />
    )
  },
  {
    id: PropertyManagementSystem.Revela,
    name: PMSNames[PropertyManagementSystem.Revela],
    icon: (
      <IntegrationLogoIcon id={PropertyManagementSystem.Revela} size="BASE" />
    )
  }
];

type PmsCapability = {
  requiresBoomScreen: boolean;
  requiresBoomReport: boolean; // boom_report maps to isRentReporting in portal
};

// Maps each PMS to the product features that grant access to it.
// boom_screen = FEATURES.BOOM_SCREEN, boom_report = FEATURES.RENT_REPORTING
const PMS_CAPABILITIES: Record<string, PmsCapability> = {
  // boom_screen only
  [PropertyManagementSystem.Propertyware]: {
    requiresBoomScreen: true,
    requiresBoomReport: false
  },
  // boom_report only
  [PropertyManagementSystem.Resman]: {
    requiresBoomScreen: false,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.Entrata]: {
    requiresBoomScreen: false,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.Quext]: {
    requiresBoomScreen: false,
    requiresBoomReport: true
  },
  // both boom_screen and boom_report grant access
  [PropertyManagementSystem.Buildium]: {
    requiresBoomScreen: true,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.RentManager]: {
    requiresBoomScreen: true,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.Yardi]: {
    requiresBoomScreen: true,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.Appfolio]: {
    requiresBoomScreen: true,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.Rentvine]: {
    requiresBoomScreen: true,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.RentersWarehouse]: {
    requiresBoomScreen: true,
    requiresBoomReport: true
  },
  [PropertyManagementSystem.ManageAmerica]: {
    requiresBoomScreen: true,
    requiresBoomReport: true
  },
  // boom_report only
  [PropertyManagementSystem.Revela]: {
    requiresBoomScreen: false,
    requiresBoomReport: true
  }
};

const filterPmsIntegrationsByProducts = (
  integrations: Integration[],
  {
    isBoomScreen,
    isBoomReport
  }: { isBoomScreen: boolean; isBoomReport: boolean }
): Integration[] =>
  integrations.filter((integration) => {
    const cap = PMS_CAPABILITIES[integration.id];
    if (!cap) return false;
    return (
      (cap.requiresBoomScreen && isBoomScreen) ||
      (cap.requiresBoomReport && isBoomReport)
    );
  });

const IntegrationRow = ({
  integration,
  selectedIntegration,
  disabled,
  onSelect
}: {
  integration: Integration;
  selectedIntegration: string;
  disabled: boolean;
  onSelect: () => void;
}) => (
  <Card
    className={cx(
      "p-6 flex justify-between m-1",
      disabled && "opacity-50 bg-alpha-40"
    )}
    onClick={() => {
      if (!disabled) onSelect();
    }}
  >
    <div className="flex items-center gap-4">
      {integration.icon}
      <p className="font-bold text-base">{integration.name}</p>
    </div>
    <Radio
      className="mr-4 self-center cursor-pointer"
      id={integration.id}
      name={integration.name}
      onChange={() => null}
      checked={selectedIntegration === integration.id}
    />
  </Card>
);

export const FirstStep = ({
  selectedIntegration,
  setSelectedIntegration,
  setCurrentStep,
  closeModal
}: FirstStepProps) => {
  const { currentSettings, hubspotHubId } = useSettingsController();
  const { type, partner, refreshPartnerData } = useAuthContext();
  const isBoomFinanceEnabled = partner?.features.includes(
    FEATURES.WIP_BOOM_FINANCE
  );

  const availablePmsIntegrations = useMemo(
    () =>
      filterPmsIntegrationsByProducts(PMS_INTEGRATIONS, {
        isBoomScreen: type.isBoomScreen,
        isBoomReport: type.isRentReporting
      }).sort((a, b) => a.name.localeCompare(b.name)),
    [type.isBoomScreen, type.isRentReporting]
  );

  const { mutate: connectZillow, isPending: isConnectingZillow } = useMutation({
    mutationKey: ["ZILLOW", "CREATE"],
    mutationFn: () => zillowApi.connect(),
    onSuccess: () => {
      refreshPartnerData();
      closeModal();
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const { mutate: connectZumper, isPending: isConnectingZumper } = useMutation({
    mutationKey: ["ZUMPER", "CREATE"],
    mutationFn: () => zumperApi.connect(),
    onSuccess: () => {
      refreshPartnerData();
      closeModal();
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const { mutate: connectApartmentsCom, isPending: isConnectingApartmentsCom } =
    useMutation({
      mutationKey: ["APARTMENTS_COM", "CREATE"],
      mutationFn: () => apartmentsComApi.connect(),
      onSuccess: () => {
        refreshPartnerData();
        closeModal();
      },
      onError: (err) => {
        toast.error(getErrorMessage(err));
      }
    });

  const { mutate: connectBoomFi, isPending: isConnectingBoomFi } = useMutation({
    mutationKey: ["BOOM_FI", "CREATE"],
    mutationFn: () => boomFiApi.connect(),
    onSuccess: () => {
      refreshPartnerData();
      closeModal();
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  const othersIntegrations: Integration[] = [
    ...BASE_INTEGRATIONS,
    ...(type.isBoomCRM && !partner?.codebox_connected
      ? [
          {
            id: IntegrationId.CODEBOX,
            name: "Codebox",
            icon: <IntegrationLogoIcon id={IntegrationId.CODEBOX} size="BASE" />
          }
        ]
      : []),
    ...(type.isBoomCRM && !partner?.zillow_connected
      ? [
          {
            id: IntegrationId.ZILLOW,
            name: "Zillow",
            icon: <IntegrationLogoIcon id={IntegrationId.ZILLOW} size="BASE" />
          }
        ]
      : []),
    ...(type.isBoomCRM && !partner?.zumper_connected
      ? [
          {
            id: IntegrationId.ZUMPER,
            name: "Zumper",
            icon: <IntegrationLogoIcon id={IntegrationId.ZUMPER} size="BASE" />
          }
        ]
      : []),
    ...(type.isBoomCRM && !partner?.apartments_com_connected
      ? [
          {
            id: IntegrationId.APARTMENTS_COM,
            name: "Apartments.com",
            icon: (
              <IntegrationLogoIcon
                id={IntegrationId.APARTMENTS_COM}
                size="BASE"
              />
            )
          }
        ]
      : []),
    ...(type.isBoomScreen && isBoomFinanceEnabled
      ? [
          {
            id: IntegrationId.BOOM_FI,
            name: "BoomFinance",
            icon: <IntegrationLogoIcon id={IntegrationId.BOOM_FI} size="BASE" />
          }
        ]
      : [])
  ];

  const handleContinue = () => {
    const id = selectedIntegration as PropertyManagementSystem | IntegrationId;
    if (id === IntegrationId.ZILLOW) connectZillow();
    else if (id === IntegrationId.ZUMPER) connectZumper();
    else if (id === IntegrationId.APARTMENTS_COM) connectApartmentsCom();
    else if (id === IntegrationId.BOOM_FI) connectBoomFi();
    else if (DIRECT_CONNECT_INTEGRATIONS.has(id)) return;
    else setCurrentStep(Step.SECOND);
  };

  const isDisabled = (id: string) => {
    if (id === IntegrationId.GA)
      return Boolean(currentSettings.google.googleAnalyticsId);
    if (id === IntegrationId.HUBSPOT) return Boolean(hubspotHubId);
    return false;
  };

  if (type.isMultiPMS)
    return (
      <>
        {availablePmsIntegrations.length > 0 && (
          <div className="flex flex-col gap-2 flex-1 min-h-0">
            <p className="text-sm font-medium text-content-700">
              Property management system
            </p>
            <div className="flex flex-col gap-2 flex-1 min-h-0 max-h-[400px] overflow-y-auto">
              {availablePmsIntegrations.map((integration) => (
                <IntegrationRow
                  key={integration.id}
                  integration={integration}
                  selectedIntegration={selectedIntegration}
                  disabled={false}
                  onSelect={() =>
                    setSelectedIntegration(
                      integration.id as PropertyManagementSystem | IntegrationId
                    )
                  }
                />
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-2 mt-4">
          <p className="text-sm font-medium text-content-700">Others</p>
          <div className="flex flex-col gap-2 max-h-[240px] overflow-y-auto">
            {othersIntegrations.map((integration) => (
              <IntegrationRow
                key={integration.id}
                integration={integration}
                selectedIntegration={selectedIntegration}
                disabled={isDisabled(integration.id)}
                onSelect={() =>
                  setSelectedIntegration(
                    integration.id as PropertyManagementSystem | IntegrationId
                  )
                }
              />
            ))}
          </div>
        </div>

        <Button
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.BASE}
          onClick={handleContinue}
          disabled={!selectedIntegration}
          loading={
            isConnectingZillow ||
            isConnectingZumper ||
            isConnectingApartmentsCom ||
            isConnectingBoomFi
          }
          className="ml-auto mt-4"
        >
          Continue
        </Button>
      </>
    );

  // Legacy flow
  return (
    <>
      {othersIntegrations.map((integration) => {
        const disabled = isDisabled(integration.id);
        return (
          <Card
            className={cx(
              "p-6 flex justify-between m-1",
              disabled && "opacity-50 bg-alpha-40"
            )}
            key={integration.id}
            onClick={() => {
              if (!disabled)
                setSelectedIntegration(
                  integration.id as PropertyManagementSystem | IntegrationId
                );
            }}
          >
            <div className="flex items-center gap-4">
              {integration.icon}
              <p className="font-bold text-base">{integration.name}</p>
            </div>
            <Radio
              className="mr-4 self-center cursor-pointer"
              id={integration.id}
              name={integration.name}
              onChange={() => null}
              checked={selectedIntegration === integration.id}
            />
          </Card>
        );
      })}
      <Button
        variant={ButtonVariant.PRIMARY}
        size={ButtonSize.BASE}
        onClick={handleContinue}
        disabled={!selectedIntegration}
        loading={isConnectingZillow || isConnectingApartmentsCom}
        className="ml-auto mt-4"
      >
        Continue
      </Button>
    </>
  );
};
